import type { Amount, MandateView, OccurrenceView, ScheduleBlock, SignedStep, Weekday } from "../api/types.js";
import { calendarDate, formatMoney, lastDigits, sgDay } from "../lib/format.js";
import type { IconId } from "../lib/icon.js";

/**
 * DCTA-209 F55 (docs/design/v3/09-scheduled.md row 27, §2.2) — a schedule's signed fields in words. "The card says only what
 * is signed": every string here is one field of the payload's `schedule` block (or the schedule a mandate carries,
 * verbatim) put into words. It FORMATS; it never works out a date — `first_on`, `upcoming_on`, `expires_on`, `last_on` are
 * printed as the bank signed them, and a date the payload does not carry is never printed. A calendar date's weekday is
 * read from the date itself (as `lib/format.ts` reads a heading's), which is naming, not arithmetic.
 */

const WEEKDAYS: Readonly<Record<Weekday, string>> = { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" };
const BY_INDEX = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"] as const;
const CALENDAR = /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

/** "1st", "2nd", "3rd", "11th", "21st", "31st" — the day of the month as the bank signed it. */
export function ordinal(day: string | number): string {
  const n = Number(day);
  if (!Number.isInteger(n) || n < 1) return String(day);
  const mod100 = n % 100;
  const suffix = mod100 >= 11 && mod100 <= 13 ? "th" : n % 10 === 1 ? "st" : n % 10 === 2 ? "nd" : n % 10 === 3 ? "rd" : "th";
  return `${String(n)}${suffix}`;
}

export function weekdayName(w: Weekday): string {
  return WEEKDAYS[w];
}

/** A signed date as a chip shows it ("Sun" · "1 Nov") and as a screen reader says it ("Sunday 1 November"). */
export interface DateWords {
  readonly weekday: string;
  readonly day: string;
  readonly long: string;
}

export function dateWords(iso: string): DateWords | undefined {
  const m = CALENDAR.exec(iso);
  if (m === null) return undefined;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const at = new Date(Date.UTC(y, mo - 1, d));
  // A day the calendar does not have (31 Nov) rolls over in Date: refuse it rather than print another day.
  if (at.getUTCMonth() !== mo - 1) return undefined;
  const weekday = BY_INDEX[at.getUTCDay()] as string;
  const month = MONTHS[mo - 1] as string;
  return { weekday: weekday.slice(0, 3), day: `${String(d)} ${month.slice(0, 3)}`, long: `${weekday} ${String(d)} ${month}` };
}

/** "Sun 1 Nov" — a chip, a list row's next date, a history's sub-line. */
export function shortDate(iso: string): string {
  const w = dateWords(iso);
  return w === undefined ? iso : `${w.weekday} ${w.day}`;
}

/** "Fri 16 Oct 2026" — a date with its year (the rule of a single date, a flag, a history row). */
export function fullDate(iso: string): string {
  const w = dateWords(iso);
  return w === undefined ? iso : `${w.weekday} ${w.day} ${iso.slice(0, 4)}`;
}

/** "16 Oct" — the sign label of a single date. */
export function dayMonth(iso: string): string {
  return dateWords(iso)?.day ?? iso;
}

const money = (a: Amount): string => formatMoney(a.value, a.currency);
const upperFirst = (s: string): string => `${s.charAt(0).toUpperCase()}${s.slice(1)}`;

export const isBillRule = (b: ScheduleBlock): boolean => b.amount.kind !== "fixed";

/** 27a: what the card is. */
export function scheduleLead(b: ScheduleBlock): string {
  return b.cadence === "once" ? "Scheduled payment" : "Repeat payment";
}

/** 27b: "Every month on the 1st" · "Every Friday" · "Once, on Fri 16 Oct 2026". */
export function ruleLine(b: ScheduleBlock): string {
  if (b.cadence === "monthly") return `Every month on the ${ordinal(b.day_of_month ?? "")}`;
  if (b.cadence === "weekly") return b.weekday === undefined ? "Every week" : `Every ${weekdayName(b.weekday)}`;
  return `Once, on ${fullDate(b.first_on)}`;
}

export function ruleIcon(b: ScheduleBlock): IconId {
  return b.cadence === "once" ? "i-calendar" : "i-repeat";
}

/** The rule inside a sentence or a sub-line: "every month" · "every Friday" · "once" (rows 27j, 28j, 30a, 30e). */
export function ruleShort(b: ScheduleBlock): string {
  if (b.cadence === "monthly") return "every month";
  if (b.cadence === "weekly") return b.weekday === undefined ? "every week" : `every ${weekdayName(b.weekday)}`;
  return "once";
}

/** A list row's rule before its next date (28b): "Every month" · "Every Friday" · "Once". */
export function rowRule(b: ScheduleBlock): string {
  return upperFirst(ruleShort(b));
}

/** 27c: the first payment dates as chips, from `upcoming_on` — none on a single date (its rule names it). */
export function dateChips(b: ScheduleBlock): DateWords[] {
  if (b.cadence === "once") return [];
  return (b.upcoming_on ?? []).flatMap((iso) => {
    const w = dateWords(iso);
    return w === undefined ? [] : [w];
  });
}

/** One key–value row of the schedule block (a value and, under it, its small line). */
export interface SchedKV {
  readonly k: string;
  readonly v: string;
  readonly small?: string;
}

/** 27c without B70: a "First" row from `first_on` in the chips' place — the app never computes a later date. */
export function firstRow(b: ScheduleBlock): SchedKV | undefined {
  if (b.cadence === "once" || (b.upcoming_on ?? []).length > 0) return undefined;
  return { k: "First", v: fullDate(b.first_on) };
}

const payments = (n: string): string => `${n} ${n === "1" ? "payment" : "payments"}`;

/** 27d: Ends — open (with its horizon), after a count, or until a date. A single date has none. */
export function endsRow(b: ScheduleBlock): SchedKV | undefined {
  if (b.cadence === "once") return undefined;
  const e = b.ends;
  if (e.kind === "open") return { k: "Ends", v: "When you stop it", small: `Or by itself on ${calendarDate(b.expires_on)}` };
  if (e.kind === "count") return { k: "Ends", v: `After ${payments(e.count)}` };
  return { k: "Ends", v: `Until ${calendarDate(e.last_on)}` };
}

/** 27e: "12 payments · S$2,400.00" — none for a single date, none for a bill rule. */
export function atMostRow(b: ScheduleBlock): { readonly k: string; readonly count: string; readonly total: string } | undefined {
  if (b.cadence === "once" || isBillRule(b)) return undefined;
  return { k: "At most", count: payments(b.occurrences_max), total: money(b.total_max) };
}

const BILL_RULE = { bill_statement: "the full statement balance", bill_minimum: "the minimum payment" } as const;

/** A card bill's rule in a sentence: "the full statement balance", "the minimum payment"; undefined for a set amount. */
export function billRule(b: ScheduleBlock): string | undefined {
  return b.amount.kind === "fixed" ? undefined : BILL_RULE[b.amount.kind];
}

/** 27g: a bill rule's signed cap — the row's figure after "up to"; undefined for a set amount. */
export function upTo(b: ScheduleBlock): string | undefined {
  return b.amount.kind === "fixed" ? undefined : money(b.amount.cap);
}

/**
 * DCTA-209 qa209 (prod QA D1): a bill rule's cap in words — the signed figure exactly (the row's "up to"), per payment.
 * Never "your card limit": the customer's own most ("…but never more than 1,000") is signed as the cap, and the payload
 * does not say whose figure it is, so the line names the figure — true whoever set it. Undefined for a set amount.
 */
function capLine(b: ScheduleBlock): string | undefined {
  const cap = upTo(b);
  return cap === undefined ? undefined : `never more than ${cap} a payment`;
}

/** 27f: a card bill's rule, each time, and its signed cap (qa209 D1). */
export function eachTimeRow(b: ScheduleBlock): SchedKV | undefined {
  const rule = billRule(b);
  const cap = capLine(b);
  return rule === undefined || cap === undefined ? undefined : { k: "Each time", v: upperFirst(rule), small: upperFirst(cap) };
}

/**
 * 27h: the footer's first line, in the place of the money leaving. `createdAt` is the payload's own time: "today" is that
 * day in Singapore (the schedule's time zone), so the line is a fact about what is signed, never about the phone's clock.
 */
export function footerLine(b: ScheduleBlock, createdAt: string): { readonly lead: string; readonly rest?: string } {
  if (firstIsToday(b, createdAt)) return { lead: "The first payment goes today." };
  return b.cadence === "once" ? { lead: "Nothing leaves today.", rest: " You can stop it any time before then." } : { lead: "Nothing leaves today." };
}

/** The first payment falls on the day the payload was made, in Singapore (the schedule's time zone). */
export function firstIsToday(b: ScheduleBlock, createdAt: string): boolean {
  return b.first_on === sgDay(createdAt);
}

/** 27i: the token line's lead on a schedule — no figure; it points at what the card shows above it. */
export function tokenLead(b: ScheduleBlock): string {
  return isBillRule(b) ? "Payments this size" : "This much in all";
}

/** 27k: the sign label, in parts so the figure is set as money: "Sign · " + "S$200.00" + " a month". */
export function signLabel(b: ScheduleBlock, step: SignedStep): { readonly lead: string; readonly money?: string; readonly rest?: string } {
  if (isBillRule(b)) {
    if (b.cadence === "monthly") return { lead: "Sign · every month" };
    if (b.cadence === "weekly") return { lead: "Sign · every week" };
    return { lead: `Sign · on ${dayMonth(b.first_on)}` };
  }
  const amount = step.type === "funds_transfer" || step.type === "bill_payment" ? money(step.amount) : undefined;
  if (amount === undefined) return { lead: "Sign" };
  const rest = b.cadence === "monthly" ? " a month" : b.cadence === "weekly" ? " a week" : ` on ${dayMonth(b.first_on)}`;
  return { lead: "Sign · ", money: amount, rest };
}

/**
 * The token's Confirm after the passkey on a schedule card, in the sign label's words (27k): "Confirm · S$200.00 a month",
 * "Confirm · every month" — never the bill as it stood on the day of signing (decision 8).
 */
export function confirmParts(b: ScheduleBlock, step: SignedStep): { readonly money: string; readonly rest: string } {
  const s = signLabel(b, step);
  return s.money === undefined ? { money: "", rest: s.lead.replace(/^Sign · /, "") } : { money: s.money, rest: s.rest ?? "" };
}

/** How a schedule's payment is titled, as its card named the step (30a): "Send to Mum", "Pay Platinum Card", "Move to Joint Account". */
export function mandateTitle(m: MandateView): string {
  if (m.to.kind === "biller") return `Pay ${m.to.display_name}`;
  if (m.to.kind === "own_account") return `Move to ${m.to.display_name}`;
  return `Send to ${mandateName(m)}`;
}

/** A payment that ran, titled as its receipt says it (30b): "Sent to Mum", "Paid Platinum Card", "Moved to Joint Account". */
export function mandateDoneTitle(m: MandateView): string {
  if (m.to.kind === "biller") return `Paid ${m.to.display_name}`;
  if (m.to.kind === "own_account") return `Moved to ${m.to.display_name}`;
  return `Sent to ${mandateName(m)}`;
}

/** A schedule's destination as the card names them: the customer's own word when signed, else the registry's. */
export function mandateName(m: MandateView): string {
  return m.to.nickname ?? m.to.display_name;
}

/** A payment's amount: its own (the bank's, once it decided it), else the schedule's set amount — a bill rule has none before. */
export function paymentAmount(m: MandateView, o?: Pick<OccurrenceView, "amount">): Amount | undefined {
  return o?.amount ?? (m.amount.kind === "fixed" ? m.amount : undefined);
}

/** The account a schedule pays from, as its receipt and its screen show it: "Everyday Savings ···5101". */
export function mandateFrom(m: MandateView): { readonly k: string; readonly v: string } {
  return { k: "From", v: `${m.from.display_name} ${lastDigits(m.from.masked_number)}` };
}

/** A mandate's amount in a sentence: "S$200.00", or a bill rule's "up to S$10,000.00". */
export function mandateAmount(m: MandateView): string {
  return m.amount.kind === "fixed" ? formatMoney(m.amount.value, m.amount.currency) : `up to ${money(m.amount.cap)}`;
}

/** 27j: the schedule a change replaces, as route 2 reads it. */
export function replaceLine(old: MandateView): { readonly lead: string; readonly rest: string } {
  return { lead: `Replaces your ${mandateAmount(old)} ${ruleShort(old.schedule)} to ${mandateName(old)}.`, rest: " That one stops when you sign this." };
}

/** 27l: the opened row's schedule words, in the place of "arrives now" — the due time printed as signed (F65). */
export function detailTail(b: ScheduleBlock): string[] {
  const at = b.due_at_local;
  const when = b.cadence === "once" ? `on ${shortDate(b.first_on)}` : "on each date";
  const what = billRule(b);
  const cap = capLine(b);
  if (what !== undefined && cap !== undefined) return [`pays ${what} at ${at} ${when}, ${cap}`];
  return [`goes at ${at} ${when}`, b.cadence === "once" ? "if there isn't enough money then, it's skipped" : "if there isn't enough money then, that payment is skipped"];
}
